import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Analytics } from '../utils/analytics';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState('');
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.success) {
        if (response.data.requiresVerification) {
          // 2FA aktiviert - zeige Code-Eingabe
          setUserId(response.data.data.userId);
          setRequiresVerification(true);
        } else {
          // Kein 2FA - direkt einloggen
          localStorage.setItem('accessToken', response.data.data.accessToken);
          localStorage.setItem('refreshToken', response.data.data.refreshToken);
          localStorage.setItem('user', JSON.stringify(response.data.data.user));
          Analytics.userLogin(false);
          onLogin();
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-code', { 
        userId, 
        code: verificationCode 
      });
      
      if (response.data.success) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        Analytics.userLogin(true);
        onLogin();
        navigate('/dashboard');
      }
    } catch (err: any) {
      // Fehler anzeigen, aber im Verification-Screen bleiben
      setError(err.response?.data?.message || 'Code-Verifizierung fehlgeschlagen. Bitte versuche es erneut.');
      setVerificationCode(''); // Code-Eingabe zurücksetzen
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '420px',
        width: '100%',
        padding: '40px',
        animation: 'slideIn 0.5s ease-out'
      }}>
        {/* Logo/Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            boxShadow: '0 8px 16px rgba(15, 32, 39, 0.5)',
            transform: 'rotate(-5deg)',
            transition: 'transform 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(0deg) scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(-5deg)'}
          >
            💰
          </div>
          <h1 style={{ 
            marginBottom: '10px', 
            background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '32px',
            fontWeight: 'bold'
          }}>
            MeineGroschen
          </h1>
          <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
            {requiresVerification 
              ? '📧 Prüfe deine E-Mails!' 
              : 'Willkommen zurück! Bitte melde dich an.'}
          </p>
        </div>

        {!requiresVerification ? (
          // Schritt 1: E-Mail & Passwort
          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: '600', 
                color: '#495057',
                marginBottom: '8px',
                display: 'block'
              }}>
                📧 E-Mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#2c5364';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(44, 83, 100, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                placeholder="deine@email.de"
              />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: '600', 
                color: '#495057',
                marginBottom: '8px',
                display: 'block'
              }}>
                🔒 Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#2c5364';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(44, 83, 100, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                textAlign: 'center',
                animation: 'shake 0.5s'
              }}>
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(15, 32, 39, 0.4)',
                marginTop: '10px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 32, 39, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(15, 32, 39, 0.4)';
              }}
            >
              {loading ? '⏳ Lädt...' : '🚀 Anmelden'}
            </button>
          </form>
        ) : (
          // Schritt 2: Verifizierungscode
          <form onSubmit={handleVerifyCode}>
            <div style={{
              background: 'linear-gradient(135deg, #0f202715 0%, #203a4315 50%, #2c536415 100%)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#495057' }}>
                📬 Ein 6-stelliger Code wurde an <br/>
                <strong>{email}</strong> gesendet
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
                Code ist 10 Minuten gültig
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                fontWeight: '600', 
                color: '#495057',
                marginBottom: '8px',
                display: 'block'
              }}>
                🔑 Verifizierungscode
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                disabled={loading}
                maxLength={6}
                placeholder="123456"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  fontSize: '24px',
                  letterSpacing: '8px',
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                textAlign: 'center',
                animation: 'shake 0.5s'
              }}>
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              style={{
                width: '100%',
                padding: '14px',
                background: (loading || verificationCode.length !== 6) ? '#ccc' : 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(15, 32, 39, 0.4)',
                marginTop: '10px'
              }}
              onMouseEnter={(e) => {
                if (!loading && verificationCode.length === 6) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 32, 39, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(15, 32, 39, 0.4)';
              }}
            >
              {loading ? '⏳ Verifiziere...' : '✅ Code bestätigen'}
            </button>

            <button
              type="button"
              onClick={() => {
                setRequiresVerification(false);
                setVerificationCode('');
                setError('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: '#667eea',
                border: '2px solid #e9ecef',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '10px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2c5364';
                e.currentTarget.style.background = 'rgba(44, 83, 100, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e9ecef';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ← Zurück zum Login
            </button>
          </form>
        )}

        {!requiresVerification && (
          <div style={{
            textAlign: 'center',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid #e9ecef'
          }}>
            <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
              Noch kein Konto?{' '}
              <Link 
                to="/register" 
                style={{ 
                  color: '#2c5364',
                  fontWeight: 'bold',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Jetzt registrieren →
              </Link>
            </p>
          </div>
        )}

        {/* Footer Links */}
        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          paddingTop: '15px',
          borderTop: requiresVerification ? 'none' : '1px solid #e9ecef',
          fontSize: '12px',
          color: '#6c757d'
        }}>
          <Link 
            to="/imprint" 
            style={{ 
              color: '#6c757d',
              textDecoration: 'none',
              marginRight: '15px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#2c5364'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
          >
            Impressum
          </Link>
          <Link 
            to="/privacy" 
            style={{ 
              color: '#6c757d',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#2c5364'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
          >
            Datenschutz
          </Link>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
      `}</style>
    </div>
  );
};

export default Login;
